import React, { useEffect, useState } from 'react';
import { challengesAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import HLSPlayer from '../../components/HLSPlayer';
import { motion } from 'framer-motion';
import { Trophy, MessageSquare, Clock, CheckCircle2, X, Send, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function CoachChallengeSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [viewing, setViewing] = useState(null);
  const [response, setResponse] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await challengesAPI.allSubmissions(filter === 'all' ? null : filter);
      setSubmissions(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleDownload = async (videoFilename, playerName, week) => {
    const token = localStorage.getItem('elev802_token');
    const backend = process.env.REACT_APP_BACKEND_URL;
    const safeName = (playerName || 'player').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const desiredFilename = `challenge_week${week}_${safeName}.mp4`;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const toastId = toast.loading('Preparing download...');
    try {
      const safeKey = videoFilename.split('/').map(encodeURIComponent).join('/');
      if (isMobile) {
        const linkResp = await fetch(`${backend}/api/admin/download-link/${safeKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!linkResp.ok) throw new Error(`HTTP ${linkResp.status}`);
        const { url } = await linkResp.json();
        toast.success('Opening download...', { id: toastId });
        window.location.href = `${backend}${url}`;
        return;
      }
      const resp = await fetch(`${backend}/api/admin/download-video/${safeKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = desiredFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Download ready', { id: toastId });
    } catch (e) {
      toast.error('Download failed', { id: toastId });
    }
  };

  const openSubmission = (sub) => {
    setViewing(sub);
    setResponse(sub.coach_response || '');
  };

  const handleReview = async () => {
    if (!response.trim()) { toast.error('Write a response first'); return; }
    setSaving(true);
    try {
      await challengesAPI.reviewSubmission(viewing.id, { coach_response: response, status: 'reviewed' });
      toast.success('Feedback sent');
      setViewing(null);
      setResponse('');
      load();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <Layout title="CHALLENGE SUBMISSIONS" showBack>
      <div className="px-4 py-6 space-y-4">
        <h2 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight">SUBMISSIONS</h2>

        {/* Filter */}
        <div className="flex gap-1 bg-[#F1F5F9] rounded-lg p-1">
          {[
            { id: 'pending', label: 'Pending', icon: Clock },
            { id: 'reviewed', label: 'Reviewed', icon: CheckCircle2 },
            { id: 'all', label: 'All', icon: Trophy },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setFilter(id)}
              className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors ${
                filter === id ? 'bg-white text-[#0A2242] shadow-sm' : 'text-[#64748B]'
              }`}
              data-testid={`filter-${id}`}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : submissions.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
            <Trophy size={32} className="mx-auto text-[#CBD5E1] mb-2" />
            <p className="text-[#64748B] text-sm">No {filter === 'all' ? '' : filter + ' '}submissions yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {submissions.map((s) => (
              <button
                key={s.id}
                onClick={() => openSubmission(s)}
                className="w-full bg-white border border-[#E2E8F0] hover:border-[#FF9100]/40 rounded-xl p-4 text-left transition-colors"
                data-testid={`submission-${s.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase tracking-widest text-[#FF9100] font-bold">
                    Week {s.challenge_week} · {s.user_name}
                  </p>
                  <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    s.status === 'reviewed' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#2563EB]/10 text-[#2563EB]'
                  }`}>
                    {s.status === 'reviewed' ? 'Reviewed' : 'Pending'}
                  </span>
                </div>
                <h4 className="font-['Barlow_Condensed'] text-sm font-bold tracking-tight">{s.challenge_title}</h4>
                {s.notes && <p className="text-[10px] text-[#64748B] mt-1 line-clamp-1">"{s.notes}"</p>}
                <p className="text-[9px] text-[#94A3B8] mt-1">{new Date(s.submitted_at).toLocaleDateString()}</p>
              </button>
            ))}
          </div>
        )}

        {/* View / Review Modal */}
        {viewing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-3 overflow-y-auto" onClick={() => setViewing(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl my-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E8F0]">
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-[#FF9100] font-bold">Week {viewing.challenge_week} Submission</p>
                  <h3 className="font-['Barlow_Condensed'] text-lg font-bold">{viewing.user_name}</h3>
                </div>
                <div className="flex items-center gap-3">
                  {viewing.video_filename && (
                    <button
                      onClick={() => handleDownload(viewing.video_filename, viewing.user_name, viewing.challenge_week)}
                      className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#2563EB] hover:text-[#1d4ed8] transition-colors"
                      title="Download video"
                      data-testid="download-submission-btn"
                    >
                      <Download size={12} /> Download
                    </button>
                  )}
                  <button onClick={() => setViewing(null)}><X size={20} /></button>
                </div>
              </div>
              {viewing.video_filename && (
                <HLSPlayer videoFilename={viewing.video_filename} watermarkText="Coach Review" className="w-full aspect-video bg-black" />
              )}
              <div className="px-5 py-4 space-y-3">
                <p className="text-sm text-[#0A2242]"><span className="font-bold">Challenge:</span> {viewing.challenge_title}</p>
                {viewing.notes && (
                  <div className="bg-[#F1F5F9] rounded-lg p-3">
                    <p className="text-[9px] uppercase tracking-widest text-[#64748B] font-bold mb-1">Player Note</p>
                    <p className="text-xs text-[#0A2242]">"{viewing.notes}"</p>
                  </div>
                )}
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-[#64748B] font-bold mb-1">Coach Feedback</p>
                  <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder="Write feedback for this player..."
                    className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9100] resize-none h-24"
                    data-testid="response-input"
                  />
                </div>
                <button
                  onClick={handleReview}
                  disabled={saving}
                  className="w-full bg-[#FF9100] hover:bg-[#FFA733] text-white font-bold uppercase tracking-wide rounded-lg px-4 py-3 text-xs flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="send-feedback-btn"
                >
                  <Send size={14} /> {saving ? 'Sending...' : viewing.status === 'reviewed' ? 'Update Feedback' : 'Send Feedback'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </Layout>
  );
}
